import { Language, Subsidiary } from '@coolblue-development/constants';
import {
  Box,
  Button,
  Collapse,
  freddyTheme,
  Grid,
  GridItem,
  Typography
} from '@coolblue-development/freddy';
import reserveItemInAStoreTrigger from '@coolblue-development/ga4-events/triggers/StoreStockOverlay/reserveItemInAStoreTrigger';
import selectMoreLessOpeningTimesTrigger from '@coolblue-development/ga4-events/triggers/StoreStockOverlay/selectMoreLessOpeningTimesTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { useClientLogger } from '@coolblue-development/next-config/client';
import {
  formatFullDate,
  formatFullTextDay,
  formatTime
} from '@coolblue-development/next-formatting-utils/core';
import { SelectableBox } from '@coolblue-development/next-patterns/client';
import { useRequestContext } from '@coolblue-development/next-request-context/client';
import styled from '@emotion/styled';
import StoreAvailabilityState from '@features/product/components/StoreStockModal/StoreAvailabilityState';
import { AvailabilityState } from '@features/product/typings';
import {
  StoreAvailability,
  StoreSchedule
} from '@features/product-page/api/queries/productStoreAvailabilitiesQuery';
import { useTranslation } from '@i18n/useTranslation';
import { addProductToShoppingCart } from '@utils/nextShoppingCartActions/addProductToShoppingCart';
import { useEffect, useState } from 'react';
type Props = {
  isActive: boolean;
  onlineAvailabilityState: AvailabilityState;
  productId: number;
  setIsActive: () => void;
  storeAvailability: StoreAvailability;
  releaseDate?: string;
  trackSelectStore: (stockLocationId?: string, storeName?: string) => void;
  dateOverride?: string;
};

function StoreAvailabilityCard({
  isActive,
  onlineAvailabilityState,
  setIsActive,
  productId,
  storeAvailability: { stockLocationId, store, availabilityState },
  releaseDate,
  trackSelectStore,
  dateOverride
}: Props) {
  const logger = useClientLogger();
  const { trackEvent } = useAnalytics();
  const [isLoading, setIsLoading] = useState(false);
  const [isShowingSchedule, setIsShowingSchedule] = useState(false);
  const { language, subsidiary } = useRequestContext();
  const { t } = useTranslation('webshop.productPage');
  const isPreOrder = !!releaseDate && onlineAvailabilityState === AvailabilityState.PreOrder;
  const isAvailableOnline = onlineAvailabilityState === AvailabilityState.OnStock;

  useEffect(() => {
    if (!isActive) {
      setIsShowingSchedule(false);
    }
  }, [isActive]);

  const trackingAddToCart = () => {
    trackEvent(
      reserveItemInAStoreTrigger({
        sub_feature: 'generic_store_stock_overlay',
        store_id: Number(stockLocationId),
        store_name: store?.name ?? '',
        item_id: productId
      })
    );
  };

  const trackSchedule = () => {
    trackEvent(selectMoreLessOpeningTimesTrigger({ content: 'more' }));
  };
  const onAddToCartButtonClick = (productId: number, stockLocationId: number) => () => {
    const params = {
      locationId: stockLocationId
    };

    setIsLoading(true);
    trackingAddToCart();
    addProductToShoppingCart({ productId, params, revalidate: false })
      .then(payload => {
        setIsLoading(false);
        const redirectUrl = payload?.data?.redirectUrl;

        if (redirectUrl) {
          window.location.assign(redirectUrl);
        }
      })
      .catch(error => {
        logger.error(`Failed to add product to cart`, error);

        setIsLoading(false);
      });
  };

  function renderStoreAddress(store: StoreAvailability['store']) {
    if (!store?.address) return null;

    const { address } = store;

    return (
      <Typography mb={0} variant='body'>
        {address.street} {address.houseNumber}
        {address.houseNumberAddition}, {address.postalCode} {address.city}
      </Typography>
    );
  }

  function renderOpeningTime(openingHour: StoreSchedule | null, fontWeight: string) {
    if (!openingHour) return null;

    if (openingHour.isClosed) {
      return <Typography fontWeight={fontWeight}>{t('storePickup.closed')}</Typography>;
    }

    const openingTime = getFormattedTime(openingHour, language, subsidiary, true);
    const closingTime = getFormattedTime(openingHour, language, subsidiary, false);

    return (
      <Typography fontWeight={fontWeight}>
        {openingTime} - {closingTime}
      </Typography>
    );
  }

  const currentDateIndex = getDateIndex(new Date());

  if (!store?.schedules) return <></>;

  const nextOpeningDateIndex = getNextOpeningDateIndex(
    store.schedules,
    currentDateIndex,
    releaseDate
  );

  if (!store.schedules[currentDateIndex] || !store.schedules[nextOpeningDateIndex]) {
    return <></>;
  }

  const isClosed = isStoreClosed(store.schedules[currentDateIndex]);
  const openingHours = store.schedules.slice(currentDateIndex, currentDateIndex + 7);

  const ScheduleCollapsible = isClosed ? StyledClosedStoreCollapse : StyledOpenStoreCollapse;
  const nextOpeningTime = getFormattedTime(
    store.schedules[nextOpeningDateIndex],
    language,
    subsidiary,
    true
  );
  const nextClosingTime = getFormattedTime(
    store.schedules[nextOpeningDateIndex],
    language,
    subsidiary,
    false
  );

  const collapsibleText = isClosed
    ? t('storePickup.storeClosed', {
        openingTime: nextOpeningTime
      })
    : t('storePickup.storeOpen', {
        closingTime: getFormattedTime(
          store.schedules[currentDateIndex],
          language,
          subsidiary,
          false
        )
      });

  const collapseTranslations = {
    labelCollapsed: collapsibleText,
    labelExpanded: collapsibleText
  };

  return (
    <Box mt={200}>
      <SelectableBox
        value={String(stockLocationId)}
        onClick={() => trackSelectStore(stockLocationId, store?.name)}
      >
        <Grid
          templateColumns={{
            xs: '1fr',
            md: '1fr 1fr'
          }}
          templateRows='auto 1fr'
          templateAreas={{
            xs: `
              "title"
              "address"
              "availability"
            `,
            md: `
              "title title"
              "address availability"
            `
          }}
          columnGap={400}
        >
          <GridItem area='title'>
            <Typography variant='h3'>{store?.name}</Typography>
          </GridItem>
          <GridItem area='address'>
            {renderStoreAddress(store)}

            {isPreOrder ? (
              <Typography>
                {`${formatFullDate({
                  date: new Date(releaseDate),
                  language,
                  subsidiary
                })}, ${nextOpeningTime} - ${nextClosingTime}`}
              </Typography>
            ) : (
              <ScheduleCollapsible
                margin={0}
                togglePosition='top'
                open={isShowingSchedule}
                translations={collapseTranslations}
                onToggle={open => {
                  setIsShowingSchedule(open);
                  trackSchedule();
                  if (open) {
                    setIsActive();
                  }
                }}
              >
                {openingHours.map((openingHour, index) => {
                  const fontWeight =
                    index === 0
                      ? freddyTheme.typography.fontWeight.openSans.bold
                      : freddyTheme.typography.fontWeight.openSans.normal;

                  return (
                    <Grid templateColumns='1fr auto' key={`${store?.name}-${index}`}>
                      <GridItem>
                        <Typography fontWeight={fontWeight} sx={{ textTransform: 'capitalize' }}>
                          {formatFullTextDay({
                            date: new Date(openingHour?.scheduleDate ?? ''),
                            language,
                            subsidiary
                          })}
                        </Typography>
                      </GridItem>
                      <GridItem>{renderOpeningTime(openingHour, fontWeight)}</GridItem>
                    </Grid>
                  );
                })}
              </ScheduleCollapsible>
            )}
          </GridItem>
          <GridItem area='availability'>
            <Typography
              mt={{
                xs: 200,
                md: 0
              }}
              variant='h5'
            >
              <StoreAvailabilityState
                availabilityState={availabilityState as AvailabilityState}
                isAvailableOnline={isAvailableOnline}
                openingTime={nextOpeningTime}
                isClosed={isClosed}
                releaseDate={releaseDate}
                dateOverride={dateOverride}
                language={language}
                openingHours={openingHours}
              />
            </Typography>
          </GridItem>
        </Grid>

        {isActive &&
          shouldShowOrderButton(availabilityState as AvailabilityState, isAvailableOnline) && (
            <Button
              mt={300}
              onClick={onAddToCartButtonClick(productId, Number(stockLocationId))}
              loading={isLoading}
              variant='action'
            >
              {t('storePickup.addToCart')}
            </Button>
          )}
      </SelectableBox>
    </Box>
  );
}

const shouldShowOrderButton = (
  availabilityState: AvailabilityState,
  isAvailableOnline: boolean
) => {
  return (
    isAvailableOnline ||
    availabilityState === AvailabilityState.OnStock ||
    availabilityState === AvailabilityState.PreOrder
  );
};

const getDateIndex = (date: Date): number => {
  const currentDay = date.getDay();

  return currentDay === 0 ? 6 : currentDay - 1;
};

const getNextOpeningDateIndex = (
  openingHours: Array<StoreSchedule | null>,
  currentDateIndex: number,
  releaseDate?: string
): number => {
  if (releaseDate) {
    return getDateIndex(new Date(releaseDate));
  }

  let nextAvailableDateIndex = currentDateIndex;
  let isClosed = true;

  while (isClosed && nextAvailableDateIndex < openingHours.length) {
    isClosed = Boolean(openingHours?.[nextAvailableDateIndex]?.isClosed);

    if (isClosed) {
      nextAvailableDateIndex++;
    }
  }

  return nextAvailableDateIndex;
};

const isStoreClosed = ({ scheduleDate, closeTime, isClosed }: StoreSchedule): boolean => {
  if (!closeTime || isClosed) {
    return true;
  }

  const closingDate = new Date(scheduleDate);
  setTime(closingDate, closeTime);

  return getPreciseHours(new Date()) > getPreciseHours(closingDate);
};

const getPreciseHours = (date: Date): number => {
  return date.getHours() + date.getMinutes() / 60;
};

const getFormattedTime = (
  { scheduleDate, closeTime, openTime }: StoreSchedule,
  language: Language,
  subsidiary: Subsidiary,
  isOpening: boolean
): string => {
  const date = new Date(scheduleDate);

  setTime(date, isOpening ? openTime : closeTime);

  return formatTime({
    date,
    language,
    subsidiary
  });
};

const setTime = (date: Date, time?: string | null) => {
  const chunks = time?.split(':');

  if (chunks && chunks.length > 2) {
    date.setHours(Number(chunks[0]), Number(chunks[1]), Number(chunks[2]));
  }
};

const StyledClosedStoreCollapse = styled(Collapse)`
  span {
    color: ${({ theme }) => theme.color.font.sentimentNegative} !important;
  }

  svg {
    margin-left: ${({ theme }) => theme.spacing[200]};
    color: ${({ theme }) => theme.color.icon.interactiveSubtle} !important;
  }

  button {
    flex-direction: row-reverse;
    align-self: flex-start;

    &:hover {
      text-decoration: none !important;
    }
  }
`;

const StyledOpenStoreCollapse = styled(Collapse)`
  span {
    color: ${({ theme }) => theme.color.font.sentimentPositiveContrast} !important;
  }

  svg {
    margin-left: ${({ theme }) => theme.spacing[200]};
    color: ${({ theme }) => theme.color.icon.interactiveSubtle} !important;
  }

  button {
    flex-direction: row-reverse;
    align-self: flex-start;

    &:hover {
      text-decoration: none !important;
    }
  }
`;

export default StoreAvailabilityCard;
